<template>
  <div>
    <!-- Male Plot -->
    <div
      v-if="showMode1Plot && patientSex === 'male'"
      class="col-lg-12 col-md-12 col-sm-12"
    >
      <div class="card">
        <div class="header">
          <span v-if="mode1PlotError" class="error-message">
            {{ mode1PlotError }}
          </span>
        </div>
        <div class="body" v-if="!mode1PlotError">
          <v-chart
            ref="maleplot"
            class="chart"
            :option="maleMode1PlotChart"
            autoresize
          />
        </div>
      </div>
    </div>

    <!-- Female Plot -->
    <div
      v-if="showMode1Plot && patientSex === 'female'"
      class="col-lg-12 col-md-12 col-sm-12"
    >
      <div class="card">
        <div class="header">
          <span v-if="mode1PlotError" class="error-message">
            {{ mode1PlotError }}
          </span>
        </div>
        <div class="body" v-if="!mode1PlotError">
          <v-chart
            ref="femalePlot"
            class="chart"
            :option="femaleMode1PlotChart"
            autoresize
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VChart from "vue-echarts";
import html2canvas from "html2canvas";

export default {
  components: {
    "v-chart": VChart,
  },
  props: {
    patientSex: String,
    patient: {
      type: Array,
      default: () => [],
    },
    showMode1Plot: {
      type: Boolean,
      default: false,
    },
    chosenLandmarkId: Number,
    calculateAge: Function,
    crossImage: String,
  },
  computed: {
    sex() {
      return this.patient[0]?.sex || "";
    },
  },
  data() {
    return {
      mode1PlotError: null,
      maleMode1PlotChart: {
        title: {
          text: "Male Facial Growth",
          left: "center",
          top: "10px",
          textStyle: { fontSize: 16 },
        },
        tooltip: {
          trigger: "item",
          formatter: (params) =>
            `Age: ${params.data[0]}, Mode1: ${params.data[1]}`,
        },
        legend: {
          data: ["Male", "Patient Mode1", "Mean Line"],
          top: "30px",
        },
        xAxis: {
          type: "value",
          min: 4,
          max: 24,
          interval: 5,
          
          name: "Age",
          nameLocation: "middle",
          nameGap: 20,
          axisLabel: { color: "#333" },
          axisLine: { lineStyle: { color: "#ddd" } },
        },
        yAxis: {
          type: "value",
          name: "Mode 1",
          inverse: true, 
          min: -3,
          max: 2,
          axisLabel: { color: "#333" },
          axisLine: { lineStyle: { color: "#ddd" } },
        },
        series: [
          {
            name: "Male",
            type: "scatter",
            data: [],
            itemStyle: { color: "#69cdff" },
          },
          {
            name: "Patient Mode1",
            type: "scatter",
            data: [],
            symbol: `image://${this.crossImage}`,
            symbolSize: 15,
            itemStyle: { color: "red" },
          },
          {
            name: "Mean Line",
            type: "line",
            data: [],
            lineStyle: {
              color: "blue",
              width: 2,
              type: "solid",
            },
          },
        ],
      },
      femaleMode1PlotChart: {
        title: {
          text: "Female Facial Growth",
          left: "center",
          top: "10px",
          textStyle: { fontSize: 16 },
        },
        tooltip: {
          trigger: "item",
          formatter: (params) =>
            `Age: ${params.data[0]}, Mode1: ${params.data[1]}`,
        },
        legend: {
          data: ["Female", "Patient Mode1", "Mean Line"],
          top: "30px",
        },
        xAxis: {
          type: "value",
          min: 0,
          max: 70,
          interval: 5,
          name: "Age",
          nameLocation: "middle",
          nameGap: 20,
          axisLabel: { color: "#333" },
          axisLine: { lineStyle: { color: "#ddd" } },
        },
        yAxis: {
          type: "value",
          name: "Mode 1",
          min: -4,
          max: 3,
          axisLabel: { color: "#333" },
          axisLine: { lineStyle: { color: "#ddd" } },
        },
        series: [
          {
            name: "Female",
            type: "scatter",
            data: [],
            itemStyle: { color: "#fca3f0" },
          },
          {
            name: "Patient Mode1",
            type: "scatter",
            data: [],
            symbol: `image://${this.crossImage}`,
            symbolSize: 16,
            itemStyle: { color: "red" },
          },
          {
            name: "Mean Line",
            type: "line",
            data: [],
            lineStyle: {
              color: "blue",
              width: 2,
              type: "solid",
            },
          },
        ],
      },
    };
  },
  methods: {
    async getPrincipalComponentData() {
      if (!this.chosenLandmarkId) {
        console.error("Landmark ID is not available.");
        console.log("chosenLandmarkId",this.chosenLandmarkId)
        return;
      }

      try {
        const fasdBackendURL =
          localStorage.getItem("fasdBackendURL") || "http://localhost:8000";
        const token = localStorage.getItem("userJWtoken");

        // GET request principal components
        const response = await fetch(
          `${fasdBackendURL}/api/signatures/${this.chosenLandmarkId}/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.length === 0 || !data[0].principal_components) {
          console.error("No principal components data available.");
          return;
        }

        const principalComponents = JSON.parse(data[0].principal_components);
        const firstPrincipalComponent = principalComponents[0].toFixed(4);

        const age = await this.calculateAge();

        // GET request for Mode1Percentile
        const sexParam = this.patientSex === "female" ? "f" : "m";
        const percentileResponse = await fetch(
          `${fasdBackendURL}/api/projections/Mode1Percentile?subjectMode1=${firstPrincipalComponent}&sex=${sexParam}&age=${age}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!percentileResponse.ok) {
          throw new Error(`HTTP error! status: ${percentileResponse.status}`);
        }

        const percentileData = await percentileResponse.json();
        const mode1Percentile = percentileData.mode1Percentile;
        this.$emit("Mode1Parameters", {
          firstPrincipalComponent,
          mode1Percentile,
        });

        // Prepare data point +  mode1 Percentile
        const resultArray = [age, firstPrincipalComponent, mode1Percentile];
        console.log("Result Array:", resultArray);

        const selectedPointData = {
          value: [age, firstPrincipalComponent],
          tooltip: {
            formatter: `Age: ${age}, Mode1: ${firstPrincipalComponent}, Percentile: ${mode1Percentile.toFixed(
              2
            )}%`,
          },
        };

        if (this.patientSex === "male") {
          this.maleMode1PlotChart.series[1].data = [selectedPointData];
        } else {
          this.femaleMode1PlotChart.series[1].data = [selectedPointData];
        }

        this.handleChart();
      } catch (error) {
        console.error(
          "Error fetching principal component or percentile data:",
          error
        );
      }
    },

    async getMode1PlotData() {
      if (!this.patientSex) return;

      try {
        const fasdBackendURL =
          localStorage.getItem("fasdBackendURL") || "http://localhost:8000";
        const token = localStorage.getItem("userJWtoken");
        const sexParam = this.patientSex === "female" ? "f" : "m";

        const response = await fetch(
          `${fasdBackendURL}/api/projections/Mode1Data?sex=${sexParam}&projectionModel_name=Caucasian16pts`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const scatterData = data.map((item) => [item.age, item.mode1]);

        // Mean line
        const meanLineData = [];
        scatterData.sort((a, b) => a[0] - b[0]);

        for (let age = 1; age <= 30; age++) {
          const closestIndex = scatterData.findIndex(
            (point) => point[0] >= age
          );

          if (closestIndex !== -1) {
            const start = Math.max(closestIndex - 10, 0);
            const end = Math.min(closestIndex + 10, scatterData.length);
            const ageWindow = scatterData.slice(start, end);

            // Calculate mean
            if (ageWindow.length > 0) {
              const meanMode1 =
                ageWindow.reduce((sum, point) => sum + point[1], 0) /
                ageWindow.length;
              meanLineData.push([age, meanMode1]);
            }
          }
        }
        if (this.patientSex === "male") {
          this.maleMode1PlotChart.series[0].data = scatterData;
          this.maleMode1PlotChart.series.push({
            name: "Mean Line",
            type: "line",
            data: meanLineData,
            smooth: true,
            lineStyle: {
              color: "blue",
              width: 2,
            },
            itemStyle: { color: "#FF0000" },
            showSymbol: false,
          });
        } else {
          this.femaleMode1PlotChart.series[0].data = scatterData;
          this.femaleMode1PlotChart.series.push({
            name: "Mean Line",
            type: "line",
            data: meanLineData,
            smooth: true,
            lineStyle: {
              color: "blue",
              width: 2,
            },
            itemStyle: { color: "#FF0000" },
            showSymbol: false,
          });
        }

        this.mode1PlotError = null; 
        this.handleChart();
      } catch (error) {
        console.error("Error fetching Mode1 data:", error);
        this.mode1PlotError = "Mode1Plot failed to be generated."; 
      }
    },

    handleChart() {
      console.log("handlechart start")
      this.$nextTick(async () => {
        if (this.patientSex === "male" && this.$refs.maleplot) {
          this.$refs.maleplot.resize();
          await this.captureChart(this.$refs.maleplot.$el);
        } else if (this.patientSex === "female" && this.$refs.femalePlot) {
          this.$refs.femalePlot.resize();
          await this.captureChart(this.$refs.femalePlot.$el);
        }

        window.addEventListener("resize", () => {
          if (this.patientSex === "male" && this.$refs.maleplot) {
            this.$refs.maleplot.resize();
          } else if (this.patientSex === "female" && this.$refs.femalePlot) {
            this.$refs.femalePlot.resize();
          }
        });
      });
    },

    async captureChart(element) {
      try {
        await this.$nextTick();
        setTimeout(async () => {
          const canvas = await html2canvas(element, { scale: 1 });
          const chartImage = canvas.toDataURL("image/png");
          this.$emit("updateMode1PlotImage", chartImage);
        }, 500); 
      } catch (error) {
        console.error("Error capturing chart image:", error);
      }
    },
  },
};
</script>

<style scoped>
.chart {
  height: 530px;
  width: 70%;
  margin: 0 auto;
}
.error-message {
  color: red;
  font-size: 14px;
  font-weight: bold;
  text-align: center;
}

</style>
